import { useSiteBranding } from "../../../hooks/useSiteBranding";
import Footer from "../../components/Footer";
import Header from "../../components/Header";
import { Link } from "react-router-dom";
import styles from "./terms.module.css";

const Terms = () => {

  useSiteBranding("Terms & Conditions", null);

  return (<>
    <Header />

    <div>
      <div className={`${styles.BannerSec}`}>
        <img src="/images/contact-us-banner1.jpg" alt="" />
        <div className={`${styles.BannerTitleCU}`}>
          <div className={`${styles.Container}`}>
            <h1 className={`${styles.Heading}`}>Terms & Conditions</h1>
            <p className={`${styles.BannerSubtitle}`}>The rules that govern your use of Ezy Freight Logistics</p>
          </div>
        </div>
      </div>

      <div className={`${styles.legalContent}`}>
        <div className={`${styles.Container}`}>

          <div className={`${styles.updatedRow}`}>
            <p className={`${styles.effectiveDate}`}>Effective Date: 1 May 2025</p>
            <Link to="/privacy-policy" className={`${styles.crossLink}`}>Read our Privacy Policy →</Link>
          </div>

          <p className={`${styles.intro}`}>
            Welcome to Ezy Freight Logistics ("EFL", "we", "us", "our"). Please read these Terms and
            Conditions ("Terms") carefully before using our website (www.eflogistics.io) and associated
            services (the "Services"). By accessing or using our Services, you agree to be bound by these
            Terms. If you do not agree with these Terms, you must not access or use our Services.
          </p>

          <nav className={`${styles.toc}`} aria-label="Table of contents">
            <h2>On this page</h2>
            <ul className={`${styles.tocGrid}`}>
              <li><a href="#definitions">1. Definitions</a></li>
              <li><a href="#use-of-services">2. Use of Services</a></li>
              <li><a href="#subscriptions">3. Subscriptions and Payment</a></li>
              <li><a href="#ip">4. Intellectual Property</a></li>
              <li><a href="#data-protection">5. Data Protection and Privacy</a></li>
              <li><a href="#availability">6. Service Availability</a></li>
              <li><a href="#warranties">7. Warranties and Disclaimers</a></li>
              <li><a href="#liability">8. Limitation of Liability</a></li>
              <li><a href="#termination">9. Termination</a></li>
              <li><a href="#governing-law">10. Governing Law and Jurisdiction</a></li>
              <li><a href="#changes">11. Changes to These Terms</a></li>
              <li><a href="#contact">12. Contact Us</a></li>
            </ul>
          </nav>

          <section id="definitions" className={`${styles.legalSection}`}>
            <h3 className={`${styles.sectionTitle}`}><span className={`${styles.sectionNumber}`}>1.</span> Definitions</h3>
            <ul className={`${styles.plainList}`}>
              <li><strong>"Customer", "User", "You"</strong> — any individual, company, or entity that registers for, accesses, or uses our Services.</li>
              <li><strong>"Software"</strong> — our cloud-based logistics platform, Ezy Freight Logistics.</li>
            </ul>
          </section>

          <section id="use-of-services" className={`${styles.legalSection}`}>
            <h3 className={`${styles.sectionTitle}`}><span className={`${styles.sectionNumber}`}>2.</span> Use of Services</h3>
            <ol className={`${styles.clauseList}`}>
              <li><span className={`${styles.clauseNumber}`}>2.1</span>You must be at least 18 years old and capable of entering into legally binding contracts.</li>
              <li><span className={`${styles.clauseNumber}`}>2.2</span>You agree to use the Services only for lawful purposes and in accordance with these Terms.</li>
              <li><span className={`${styles.clauseNumber}`}>2.3</span>You are responsible for keeping your login credentials confidential and must notify us immediately of any unauthorised use of your account.</li>
            </ol>
          </section>

          <section id="subscriptions" className={`${styles.legalSection}`}>
            <h3 className={`${styles.sectionTitle}`}><span className={`${styles.sectionNumber}`}>3.</span> Subscriptions and Payment</h3>
            <ol className={`${styles.clauseList}`}>
              <li><span className={`${styles.clauseNumber}`}>3.1</span>Access to the Services may require the purchase of a subscription. Details of pricing and plans are available on our website or provided to you separately.</li>
              <li><span className={`${styles.clauseNumber}`}>3.2</span>All fees are quoted in GBP and are exclusive of VAT unless otherwise stated.</li>
              <li><span className={`${styles.clauseNumber}`}>3.3</span>Payment obligations are non-cancellable, and fees paid are non-refundable except as expressly stated in these Terms.</li>
              <li><span className={`${styles.clauseNumber}`}>3.4</span>We reserve the right to change our fees or introduce new charges with prior notice.</li>
            </ol>
          </section>

          <section id="ip" className={`${styles.legalSection}`}>
            <h3 className={`${styles.sectionTitle}`}><span className={`${styles.sectionNumber}`}>4.</span> Intellectual Property</h3>
            <ol className={`${styles.clauseList}`}>
              <li><span className={`${styles.clauseNumber}`}>4.1</span>We retain all intellectual property rights in and to our Services, including but not limited to our Software, branding, and content.</li>
              <li><span className={`${styles.clauseNumber}`}>4.2</span>You are granted a limited, non-exclusive, non-transferable licence to access and use the Services for your internal business purposes.</li>
              <li><span className={`${styles.clauseNumber}`}>4.3</span>You may not copy, modify, distribute, sell, or lease any part of our Services without our prior written consent.</li>
            </ol>
          </section>

          <section id="data-protection" className={`${styles.legalSection}`}>
            <h3 className={`${styles.sectionTitle}`}><span className={`${styles.sectionNumber}`}>5.</span> Data Protection and Privacy</h3>
            <ol className={`${styles.clauseList}`}>
              <li><span className={`${styles.clauseNumber}`}>5.1</span>Your privacy is important to us. Full details of what we collect and how we use it are set out in our <Link to="/privacy-policy">Privacy Policy</Link>, which forms part of these Terms.</li>
              <li><span className={`${styles.clauseNumber}`}>5.2</span>You acknowledge that use of the Services may involve the transmission of personal data, and you warrant that you have obtained all necessary consents to do so.</li>
            </ol>
          </section>

          <section id="availability" className={`${styles.legalSection}`}>
            <h3 className={`${styles.sectionTitle}`}><span className={`${styles.sectionNumber}`}>6.</span> Service Availability</h3>
            <ol className={`${styles.clauseList}`}>
              <li><span className={`${styles.clauseNumber}`}>6.1</span>We endeavour to ensure the Services are available 24/7 but do not guarantee uninterrupted access.</li>
              <li><span className={`${styles.clauseNumber}`}>6.2</span>We may temporarily suspend access to the Services for maintenance, updates, or other reasons without prior notice.</li>
            </ol>
          </section>

          <section id="warranties" className={`${styles.legalSection}`}>
            <h3 className={`${styles.sectionTitle}`}><span className={`${styles.sectionNumber}`}>7.</span> Warranties and Disclaimers</h3>
            <ol className={`${styles.clauseList}`}>
              <li><span className={`${styles.clauseNumber}`}>7.1</span>The Services are provided on an "as is" and "as available" basis.</li>
              <li><span className={`${styles.clauseNumber}`}>7.2</span>We disclaim all warranties, whether express or implied, including but not limited to implied warranties of merchantability, fitness for a particular purpose, and non-infringement.</li>
              <li><span className={`${styles.clauseNumber}`}>7.3</span>We do not warrant that the Services will be error-free, secure, or free from viruses or other harmful components.</li>
            </ol>
          </section>

          <section id="liability" className={`${styles.legalSection}`}>
            <h3 className={`${styles.sectionTitle}`}><span className={`${styles.sectionNumber}`}>8.</span> Limitation of Liability</h3>
            <ol className={`${styles.clauseList}`}>
              <li><span className={`${styles.clauseNumber}`}>8.1</span>To the maximum extent permitted by law, EFL shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, arising out of your use of the Services.</li>
              <li><span className={`${styles.clauseNumber}`}>8.2</span>Our total liability for any claim under these Terms shall not exceed the amount paid by you to us in the 12 months preceding the event giving rise to the liability.</li>
            </ol>
          </section>

          <section id="termination" className={`${styles.legalSection}`}>
            <h3 className={`${styles.sectionTitle}`}><span className={`${styles.sectionNumber}`}>9.</span> Termination</h3>
            <ol className={`${styles.clauseList}`}>
              <li><span className={`${styles.clauseNumber}`}>9.1</span>We may suspend or terminate your access to the Services if you breach these Terms.</li>
              <li><span className={`${styles.clauseNumber}`}>9.2</span>Upon termination, your right to use the Services will immediately cease.</li>
              <li><span className={`${styles.clauseNumber}`}>9.3</span>Sections 4, 5, 7, 8, 9, and 10 shall survive termination.</li>
            </ol>
          </section>

          <section id="governing-law" className={`${styles.legalSection}`}>
            <h3 className={`${styles.sectionTitle}`}><span className={`${styles.sectionNumber}`}>10.</span> Governing Law and Jurisdiction</h3>
            <ol className={`${styles.clauseList}`}>
              <li><span className={`${styles.clauseNumber}`}>10.1</span>These Terms shall be governed by and construed in accordance with the laws of England and Wales.</li>
              <li><span className={`${styles.clauseNumber}`}>10.2</span>Any disputes arising out of or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts of England and Wales.</li>
            </ol>
          </section>

          <section id="changes" className={`${styles.legalSection}`}>
            <h3 className={`${styles.sectionTitle}`}><span className={`${styles.sectionNumber}`}>11.</span> Changes to These Terms</h3>
            <ol className={`${styles.clauseList}`}>
              <li><span className={`${styles.clauseNumber}`}>11.1</span>We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting to our website.</li>
              <li><span className={`${styles.clauseNumber}`}>11.2</span>Your continued use of the Services following the posting of changes constitutes your acceptance of such changes.</li>
            </ol>
          </section>

          <section id="contact" className={`${styles.legalSection}`}>
            <h3 className={`${styles.sectionTitle}`}><span className={`${styles.sectionNumber}`}>12.</span> Contact Us</h3>
            <p style={{ fontSize: "15.5px", lineHeight: "27px", color: "#444", marginBottom: "14px" }}>
              If you have any questions about these Terms, please get in touch:
            </p>
            <div className={`${styles.contactCard}`}>
              <p>Ezy Freight Logistics</p>
              <p>Email: <a href="mailto:admin@eflogistics.io">admin@eflogistics.io</a></p>
              <p>Registered Address: Unit 2&3 Bay Close, Progress Way, Luton, Beds, England, LU4 9UP</p>
            </div>
          </section>

        </div>
      </div>
    </div>

    <Footer />

  </>);
};

export default Terms;